import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLenis } from "lenis/react";
import { ArrowDown, Sparkle } from "@phosphor-icons/react";
import { useLang } from "@/i18n";

const HERO_IMG =
  "https://images.unsplash.com/photo-1559842453-9bb10f8c7c28?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxkYW5kZWxpb24lMjBmaWVsZCUyMHN1bnNldHxlbnwwfHx8fDE3ODkyMzk2NDF8MA&ixlib=rb-4.1.0&q=85";

function LineMask({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const lenis = useLenis();
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  const go = (href) => (e) => {
    e.preventDefault();
    if (lenis) lenis.scrollTo(href, { offset: -70, duration: 1.4 });
    else document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="top" ref={ref} className="relative overflow-hidden pt-32 lg:pt-40" data-testid="hero-section">
      <div className="pointer-events-none absolute -left-40 top-20 h-[34rem] w-[34rem] rounded-full bg-peach/60 blur-3xl" />
      <div className="pointer-events-none absolute -right-32 bottom-0 h-[28rem] w-[28rem] rounded-full bg-sage/70 blur-3xl" />

      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-6 pb-24 lg:grid-cols-12 lg:px-10 lg:pb-32">
        <motion.div style={{ y: textY }} className="relative z-10 lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="mb-8 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-moss"
            data-testid="hero-eyebrow"
          >
            <Sparkle size={14} weight="fill" className="text-stone" />
            {t.hero.eyebrow}
          </motion.p>

          <h1 className="font-serif text-5xl leading-[0.95] tracking-tight sm:text-7xl lg:text-[6.5rem]" data-testid="hero-heading">
            <LineMask delay={0.25}>{t.hero.line1}</LineMask>
            <LineMask delay={0.4}>{t.hero.line2}</LineMask>
            <LineMask delay={0.55}>
              <em className="italic font-light text-moss">{t.hero.line3}</em>
            </LineMask>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            className="mt-8 max-w-md text-base leading-relaxed text-moss md:text-lg"
            data-testid="hero-subtitle"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05 }}
            className="mt-10 flex flex-wrap items-center gap-6"
          >
            <a
              href="#contact"
              onClick={go("#contact")}
              data-testid="hero-book-session-btn"
              className="rounded-full bg-forest px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-cream transition-all duration-300 hover:bg-moss hover:scale-[1.03] active:scale-95"
            >
              {t.hero.book}
            </a>
            <a
              href="#about"
              onClick={go("#about")}
              data-testid="hero-discover-link"
              className="link-underline text-xs font-semibold uppercase tracking-[0.2em] text-forest"
            >
              {t.hero.discover}
            </a>
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 60, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.3, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <motion.div style={{ y: imgY }} className="overflow-hidden rounded-t-full border border-forest/10 shadow-[0_30px_60px_-20px_rgba(44,53,40,0.25)]">
              <img
                src={HERO_IMG}
                alt="Dandelion field glowing at sunset"
                className="aspect-[3/4] w-full scale-105 object-cover"
                data-testid="hero-image"
              />
            </motion.div>

            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 24, repeat: Infinity, ease: "linear" }}
              className="absolute -bottom-8 -left-8 hidden h-32 w-32 items-center justify-center rounded-full bg-forest text-cream md:flex"
              data-testid="hero-badge"
            >
              <svg viewBox="0 0 100 100" className="h-full w-full p-2">
                <defs>
                  <path id="circlePath" d="M 50,50 m -36,0 a 36,36 0 1,1 72,0 a 36,36 0 1,1 -72,0" />
                </defs>
                <text className="fill-cream text-[8.5px] uppercase tracking-[0.28em]">
                  <textPath href="#circlePath">{t.hero.badge}</textPath>
                </text>
              </svg>
              <Sparkle size={18} weight="fill" className="absolute" />
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.button
        onClick={go("#about")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 text-moss transition-colors hover:text-forest lg:block"
        aria-label="Scroll down"
        data-testid="hero-scroll-down"
      >
        <ArrowDown size={22} className="animate-bounce" />
      </motion.button>
    </section>
  );
}
